import { useState, useEffect, useCallback } from 'react';
import { Image, Upload, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getLogo, uploadLogo, deleteLogo } from '../../lib/api';
import { toast } from 'sonner';

export const AdminLogoPage = () => {
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchLogo = useCallback(async () => {
    try {
      const response = await getLogo();
      setLogo(response.data);
    } catch (error) {
      console.error('Error fetching logo:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogo();
  }, [fetchLogo]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo immagini consentite');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File troppo grande (max 2MB)');
      return;
    }

    setUploading(true);
    try {
      await uploadLogo(file);
      toast.success('Logo caricato con successo');
      fetchLogo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare il logo personalizzato?')) return;

    try {
      await deleteLogo();
      toast.success('Logo eliminato');
      setLogo({ has_logo: false });
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-logo">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">LOGO</h1>
          <p className="text-slate-400 text-sm">
            Carica il logo del torneo
          </p>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Image className="w-5 h-5 text-primary" />
              Logo Torneo
            </CardTitle>
            <CardDescription>
              Il logo viene mostrato nella homepage e nella navbar. Formati: JPG, PNG, WebP (max 2MB)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Logo Preview */}
            <div className="flex flex-col items-center gap-4 p-6 bg-slate-950 rounded-lg border border-slate-800">
              {logo?.has_logo ? (
                <img
                  src={`data:${logo.content_type};base64,${logo.data}`}
                  alt="Logo attuale"
                  className="w-40 h-40 object-contain"
                />
              ) : (
                <>
                  <img
                    src="/logo512.png"
                    alt="Logo predefinito"
                    className="w-40 h-40 object-contain"
                  />
                  <span className="text-xs text-slate-500">Logo predefinito</span>
                </>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex flex-col sm:flex-row gap-3">
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button
                  asChild
                  className="w-full bg-primary text-slate-950 hover:bg-primary/90 cursor-pointer"
                  disabled={uploading}
                >
                  <span>
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {logo?.has_logo ? 'Cambia Logo' : 'Carica Logo'}
                  </span>
                </Button>
              </label>

              {logo?.has_logo && (
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Ripristina Predefinito
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogoPage;
